import React, { useMemo } from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { animationTransition } from '@shared/utils/animationTransition';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { HotelTilePhotos } from './components/HotelTilePhotos';
import { HotelTileHeader } from './components/HotelTileHeader';
import { HotelTileDistance } from './components/HotelTileDistance';
import { HotelTileDescription } from './components/HotelTileDescription';
import { HotelTileTags } from './components/HotelTileTags';
import { HotelTilePricing } from './components/HotelTilePricing';
import { HotelTileCTA } from './components/HotelTileCTA';
import type { HotelTileCardProps } from './HotelTileCard.types';

export const HotelTileCard: React.FC<HotelTileCardProps> = ({
  displayName,
  description,
  starRating,
  photos,
  distance,
  pricing,
  cta,
  tags,
  layout,
  eventContext,
  onMouseEnter,
  onMouseLeave,
  onClick,
  linkProps,
  id,
}) => {
  const isMobile = useIsMobileScreen();
  const enableHorizontalView = layout?.enableHorizontalView || false;
  const enableHoverShadow = layout?.enableHoverShadow || false;
  const showIndex = layout?.showIndex;

  const shouldShowButton = cta && cta.isEnabled !== false;

  const hoverCSS = useMemo(
    () => ({
      _hover: {
        cursor: shouldShowButton ? 'pointer' : 'default',
        transform: shouldShowButton && !isMobile ? `translateY(${pxToRem(-2)})` : 'none',
        ...(enableHoverShadow && {
          boxShadow: shouldShowButton && !isMobile ? '0px 8px 32px 0px rgba(0, 0, 0, 0.12), 0px 2px 8px 0px rgba(0, 0, 0, 0.08)' : '0px 4px 16px 0px rgba(0, 0, 0, 0.08)',
        }),
      },
    }),
    [shouldShowButton, isMobile, enableHoverShadow],
  );

  const linkAttributes = useMemo(() => {
    if (!linkProps?.href) return {};
    return {
      as: 'a' as const,
      href: linkProps.href,
      target: linkProps.target || '_blank',
    };
  }, [linkProps]);

  const renderTagsOverlay = () => <HotelTileTags tags={tags} />;

  return (
    <Flex
      position="relative"
      className="group"
      onClick={onClick || cta?.onClick}
      width="100%"
      {...linkAttributes}
      __css={hoverCSS}
      backgroundColor="white"
      borderRadius={pxToRem(12)}
      boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.04)"
      flexDirection={enableHorizontalView ? 'row' : 'column'}
      justifyContent="space-between"
      transition={animationTransition('boxShadow', 'transform', {
        duration: '300ms',
      })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      overflow="hidden"
    >
      {/* Photo Section */}
      {!!photos?.length && (
        <HotelTilePhotos
          photos={photos}
          displayName={displayName}
          showIndex={showIndex}
          enableHorizontalView={enableHorizontalView}
          renderTagsOverlay={renderTagsOverlay}
        />
      )}

      {/* Content Section */}
      <Box
        width="100%"
        height="100%"
        display="flex"
        flexDirection="column"
        padding={enableHorizontalView ? pxToRem(24) : pxToRem(16.19)}
      >
        {/* Header: Name + Star Rating + Distance */}
        <Box
          display="flex"
          gap={pxToRem(4)}
          flexDirection="column"
        >
          <HotelTileHeader displayName={displayName} />
          <HotelTileDistance
            starRating={starRating}
            distance={distance}
            id={id}
            hotelLocation={layout?.hotelLocation}
            venueLocation={layout?.venueLocation}
          />
        </Box>

        {/* Description + Pricing */}
        <Box
          flex={1}
          display="flex"
          gap={pxToRem(3)}
          marginTop={pxToRem(3)}
          flexDirection="column"
        >
          {description && (
            <HotelTileDescription
              description={description}
              eventPhoto={eventContext?.eventPhoto}
              enableHorizontalView={enableHorizontalView}
            />
          )}

          {pricing && (
            <Box
              width="100%"
              {...(!enableHorizontalView && pricing.data?.price && { minHeight: pxToRem(pricing.data.strikeoutPrice ? 80 : 60) })}
            >
              <HotelTilePricing
                data={pricing.data}
                accommodationId={pricing.accommodationId}
                roomBlock={pricing.roomBlock}
                marginTop={pxToRem(4)}
              />
            </Box>
          )}
        </Box>

        {/* CTA Button */}
        {shouldShowButton && cta && (
          <Box
            marginTop="auto"
            paddingTop={enableHorizontalView ? pxToRem(16) : pxToRem(32)}
          >
            <HotelTileCTA
              title={cta.title}
              variant={cta.variant}
              onClick={cta.onClick}
            />
          </Box>
        )}
      </Box>
    </Flex>
  );
};
